import { Descriptions, Link } from "@/atoms"
import { NO_VALUE_PLACEHOLDER } from "@/const/default-values"
import { isMaliciousPackageType, isVulnerablePackageType } from "@/domain/issue"
import { getLinkByVulnerability } from "@/domain/vulnerability"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { ImpactCell } from "@/molecules/ImpactCell"
import { IntroducedThroughUndetermined } from "@/molecules/IntroducedThroughUndetermined"
import {
  AiRelatedInsightContent,
  AiRelatedInsightLabel
} from "@/organisms/Drawers/components/AiRelatedInsight"
import { OverviewCardTemplate } from "@/organisms/Drawers/components/PackageDrawer/components/OverviewCardTemplate"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { InsightsTriageCell } from "@/pages/TriagePage/components/molecules/InsightsTriageCell"
import { IntroduceThroughInsightsICell } from "@/pages/TriagePage/components/molecules/IntroduceThroughInsightsICell"
import { PackageVersions } from "../PackageVersions"

import type { DescriptionItem } from "@/atoms"
import type { Issue } from "@/domain/issue"

import commonStyles from "@/organisms/Drawers/Drawers.module.scss"
import styles from "./SummaryCard.module.scss"

type Props = {
  record: any
  // Opt-in rows. Omitted, the card renders exactly as it always has; the demo
  // Overview tab is the only caller that changes them.
  showFixVersions?: boolean
  showImpactAndDates?: boolean
  showInsights?: boolean
}

export const SummaryCard = ({
  record,
  showFixVersions,
  showInsights,
  showImpactAndDates = true
}: Props) => {
  const { t } = useTranslation()
  const { onRowClick } = useDrawer("")

  if (!record) return null

  const {
    packageName,
    introducedThroughInsights,
    cwe,
    discoveredAt,
    lastSeen,
    riskInsights
  } = record

  const { issueType } = record as Issue
  const { applicationCount } = record as any

  const someIntroducedThroughInsightsExist =
    !introducedThroughInsights.allFalse &&
    Object.keys(introducedThroughInsights).some(
      (key) =>
        !!introducedThroughInsights[
          key as keyof typeof introducedThroughInsights
        ]
    )
  const isVulnerablePackage = isVulnerablePackageType(issueType)
  const isMaliciousPackage = isMaliciousPackageType(issueType)

  const items: DescriptionItem[] = [
    {
      label: <Descriptions.Label>{t("general.type")}</Descriptions.Label>,
      children: t(`issueTypes.${record.issueType}`)
    }
  ]

  if (isVulnerablePackage || isMaliciousPackage) {
    items.push({
      label: (
        <Descriptions.Label>
          {t("general.packageVersion_other")}
        </Descriptions.Label>
      ),
      children: (
        <PackageVersions
          name={packageName}
          version={record.packageVersions.join(", ")}
          rows={isMaliciousPackage ? 1 : 2}
          displayValueInTooltip={isMaliciousPackage}
          classes={{
            container: isMaliciousPackage
              ? styles.packageVersionMalicious
              : undefined
          }}
        />
      )
    })
  }

  if (isVulnerablePackage && showFixVersions)
    items.push({
      label: (
        <Descriptions.Label>{t("general.fixedInVersion")}</Descriptions.Label>
      ),
      children: record.fixVersions?.length ? (
        <PackageVersions
          name={packageName}
          version={record.fixVersions.join(", ")}
          rows={2}
        />
      ) : (
        NO_VALUE_PLACEHOLDER
      )
    })

  if (isVulnerablePackage || showInsights) {
    items.push(
      {
        label: (
          <Descriptions.Label>
            {t("general.introducedThrough")}
          </Descriptions.Label>
        ),
        className: commonStyles.introducedThroughInsights,
        children: someIntroducedThroughInsightsExist ? (
          IntroduceThroughInsightsICell({ tag: true, t })({
            ...introducedThroughInsights
          })
        ) : (
          <IntroducedThroughUndetermined />
        )
      },
      {
        label: <AiRelatedInsightLabel />,
        children: (
          <AiRelatedInsightContent
            aiRelated={riskInsights.aiRelated}
            aiPackageCategory={riskInsights.aiPackageCategory}
          >
            {InsightsTriageCell(t)({
              tag: true,
              aiRelated: !!riskInsights.aiRelated
            })}
          </AiRelatedInsightContent>
        )
      }
    )
  }

  items.push({
    label: <Descriptions.Label>CWE</Descriptions.Label>,
    children: cwe.description ? (
      <Link
        className={styles.cweLink}
        href={getLinkByVulnerability({ cve: cwe.id })}
        target="_blank"
      >
        {cwe.id}
      </Link>
    ) : (
      NO_VALUE_PLACEHOLDER
    )
  })

  if (!isMaliciousPackage) {
    items.push({
      label: <Descriptions.Label>OWASP</Descriptions.Label>,
      children: record?.owasp ? record.owasp : NO_VALUE_PLACEHOLDER
    })
  }

  if (showImpactAndDates)
    items.push(
      {
        label: <Descriptions.Label>{t("general.impact")}</Descriptions.Label>,
        children:
          applicationCount.items?.length > 0
            ? ImpactCell({ onRowClick, t })(applicationCount, record)
            : NO_VALUE_PLACEHOLDER
      },
      {
        label: (
          <Descriptions.Label>{t("general.discoveredAt")}</Descriptions.Label>
        ),
        children: discoveredAt
          ? getDateInUserLocale(
              getDateFromSeconds(discoveredAt),
              DEFAULT_LONG_FORMAT
            )
          : NO_VALUE_PLACEHOLDER
      },
      {
        label: <Descriptions.Label>{t("general.lastSeen")}</Descriptions.Label>,
        children: lastSeen
          ? getDateInUserLocale(
              getDateFromSeconds(lastSeen),
              DEFAULT_LONG_FORMAT
            )
          : NO_VALUE_PLACEHOLDER
      }
    )

  return <OverviewCardTemplate title="Summary" items={items} />
}
